/* Gráficos del expediente — divs posicionados, sin librería y sin SVG.
 *
 * Son cuatro formas muy simples y cada una responde una pregunta concreta. No hay ejes ni leyendas
 * porque no hacen falta: cada gráfico va acompañado de la cifra escrita y de su procedencia, y el
 * dibujo solo aporta la POSICIÓN — dónde cae la mediana dentro del rango, cuánto queda de la
 * ventana, cómo se reparte el gasto. Un dibujo que hay que descifrar aquí no sirve de nada.
 *
 * Todos llevan `aria-hidden`: lo que dicen ya está escrito al lado en texto. Repetirlo en el árbol
 * de accesibilidad sería hacer que un lector de pantalla lea dos veces lo mismo.
 */
import { porcentaje } from '../api/formato'
import './Graficos.css'

const pct = (x: number) => `${Math.max(0, Math.min(100, x))}%`

/**
 * Pista de «cuánto ofertar». El eje cubre el rango REAL de las marcas (no está clavado en
 * 80–100): un ganador al 77 % no se recorta contra el borde izquierdo.
 */
export function ejeBanda(vals: number[]): { min: number; max: number } {
  const xs = vals.filter((v) => Number.isFinite(v))
  if (!xs.length) return { min: 80, max: 100 }
  const lo = Math.min(...xs)
  const min = Math.max(0, Math.floor((lo - 5) / 5) * 5)
  return { min, max: 100 }
}

export function BandaPosicion({ marcas, zona, techo }: {
  marcas: { valor: number; rotulo: string }[]
  zona?: { p25: number; p75: number }
  techo: string
}) {
  const eje = ejeBanda([
    ...marcas.map((m) => m.valor),
    ...(zona ? [zona.p25, zona.p75] : []),
  ])
  const span = Math.max(1, eje.max - eje.min)
  const donde = (v: number) => ((Math.max(eje.min, Math.min(eje.max, v)) - eje.min) / span) * 100
  const desde = zona ? donde(zona.p25) : undefined
  const hasta = zona ? donde(zona.p75) : undefined
  return (
    <div className="g-banda" aria-hidden="true">
      <span className="g-pista" />
      {desde !== undefined && hasta !== undefined && (
        <span className="g-zona" style={{ left: pct(desde), width: pct(hasta - desde) }} />
      )}
      {marcas.map((m) => (
        <span key={m.rotulo} className={`g-marca g-marca-${m.rotulo}`}
              style={{ left: pct(donde(m.valor)) }} />
      ))}
      <span className="g-rot g-rot-ini">{porcentaje(eje.min)} del techo</span>
      {marcas.map((m) => (
        <span key={`r-${m.rotulo}`} className={`g-rot g-rot-mediana g-rot-${m.rotulo}`}
              style={{ left: pct(donde(m.valor)) }}>
          {m.rotulo} {porcentaje(m.valor)}
        </span>
      ))}
      <span className="g-rot g-rot-fin">100 % = {techo}</span>
    </div>
  )
}

/**
 * La ventana para ofertar: publicación → hoy → cierre.
 *
 * Lo que se lee de un vistazo es cuánto queda, no cuánto pasó, así que la parte consumida va
 * teñida y el marcador de «hoy» es lo único destacado.
 *
 * ⚠ Los tres rótulos van en DOS renglones —los extremos arriba, «hoy» abajo bajo su marcador— y no
 * en uno. Con los tres abajo, un proceso a punto de cerrar imprimía «hoy» y «cierra» uno encima del
 * otro: en pantalla se leía «chorya». Justo el caso en que la fecha más importa.
 *
 * Y cuando el marcador se acerca a un extremo, el rótulo deja de ir centrado y se ancla a ese lado:
 * centrado en el 100 % se saldría media palabra fuera de la caja.
 */
export function LineaTiempo({ publicado, cierre }: { publicado: string | null; cierre: string | null }) {
  const a = publicado ? new Date(publicado.replace(' ', 'T')).getTime() : NaN
  const b = cierre ? new Date(cierre.replace(' ', 'T')).getTime() : NaN
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null
  const consumido = Math.max(0, Math.min(100, ((Date.now() - a) / (b - a)) * 100))
  const [borde, sitio] =
    consumido >= 88 ? [' g-rot-hoy-borde', { right: 0 }]
      : consumido <= 12 ? [' g-rot-hoy-borde', { left: 0 }]
        : ['', { left: pct(consumido) }]
  return (
    <div className="g-tiempo" aria-hidden="true">
      <span className="g-linea" />
      <span className="g-linea-usada" style={{ width: pct(consumido) }} />
      <span className="g-hito g-hito-ini" />
      <span className="g-hito g-hito-hoy" style={{ left: pct(consumido) }} />
      <span className="g-hito g-hito-fin" />
      <span className="g-rot g-rot-ini">publicado</span>
      <span className="g-rot g-rot-fin">cierra</span>
      <span className={`g-rot g-rot-hoy${borde}`} style={sitio}>hoy</span>
    </div>
  )
}

/** Reparto del gasto entre los proveedores recurrentes. La barra es la cuota; la cifra va al lado. */
export function BarrasReparto({ items }: { items: { nombre: string; valor: number; nota: string }[] }) {
  const tope = Math.max(...items.map((i) => i.valor), 1)
  return (
    <ul className="g-reparto">
      {items.map((i) => (
        <li key={i.nombre}>
          {/* El `title` porque el nombre se recorta con puntos suspensivos cuando no cabe. */}
          <span className="g-reparto-nombre" title={i.nombre}>{i.nombre}</span>
          <span className="g-reparto-barra" aria-hidden="true">
            <i style={{ width: pct((i.valor / tope) * 100) }} />
          </span>
          <span className="g-reparto-nota cifra">{i.nota}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Cómo se reparten los tiempos de pago. Apilada y no tres barras: lo que importa es la proporción
 * entre los tres tramos, y separarlas obliga a compararlas mentalmente.
 */
export function BarraPagos({ hasta60, mas90 }: { hasta60: number; mas90: number }) {
  const medio = Math.max(0, 100 - hasta60 - mas90)
  return (
    <div className="g-pagos" aria-hidden="true">
      <i className="g-pagos-pronto" style={{ width: pct(hasta60) }} />
      <i className="g-pagos-medio" style={{ width: pct(medio) }} />
      <i className="g-pagos-tarde" style={{ width: pct(mas90) }} />
    </div>
  )
}
